import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { db } from '../../../../shared/storage/firebase';
import { ClassroomTeam, PortfolioAiVersion, PortfolioPhoto } from '../../classroom/types';
import { WorldFields } from '../../../library/WorldFields';
import { safeGameUrl } from '../../../library/gameMedia';
import LibraryMedia from '../../../library/LibraryMedia';
import { LibraryItem } from '../../../library/types';

type Kind = 'player' | 'world' | 'movie' | 'comic' | 'poster' | 'shot_batch' | 'game' | 'audio' | 'model_3d';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  creation: PortfolioAiVersion | null;
  squadId: string;
  squadName: string;
  team: ClassroomTeam;
  photos: PortfolioPhoto[];
  existingStatus?: string;
  source?: Pick<LibraryItem, 'sourcePortfolioEntryId' | 'sourceLesson'>;
  shotBatch?: LibraryItem['shotBatch'];
  onNominated: (creationId: string) => void;
}

const playerInitial = {
  name: '', wears: '', carries: '', detail: '', ability: '', abilityDescription: '',
  colors: '', traits: '',
};
const worldInitial: NonNullable<LibraryItem['world']> & { name: string } = { name: '' };

export default function ShowdownCreationModal({ isOpen, onClose, creation, squadId, squadName, team, photos, existingStatus, shotBatch, source, onNominated }: Props) {
  const { currentUser } = useAuth();
  const [kind, setKind] = useState<Kind>('player');
  const [player, setPlayer] = useState(playerInitial);
  const [world, setWorld] = useState(worldInitial);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [referenceView, setReferenceView] = useState<'creation' | 'photos'>('creation');
  const [photoIndex, setPhotoIndex] = useState(0);
  const imageUrl = useMemo(() => {
    if (!creation) return '';
    return creation.posterUrl || (creation.type === 'image' ? creation.url : '') || '';
  }, [creation]);
  const referencePhotos = useMemo(() => {
    const sourceIds = new Set(creation?.sourcePhotoIds || []);
    const linked = sourceIds.size ? photos.filter(photo => sourceIds.has(photo.id)) : photos;
    return linked.length ? linked : photos;
  }, [creation?.sourcePhotoIds, photos]);

  useEffect(() => {
    setKind(shotBatch ? 'shot_batch' : creation?.type === 'audio' ? 'audio' : creation?.type === 'model_3d' ? 'model_3d' : creation?.type === 'video' ? 'movie' : ['game_link', 'claude_artifact', 'link'].includes(creation?.type || '') ? 'game' : 'player');
    setTitle(creation?.caption || creation?.filename || '');
    setDescription('');
    setReferenceView('creation');
    setPhotoIndex(0);
    setError('');
  }, [creation, shotBatch]);

  if (!isOpen || !creation) return null;

  const close = () => {
    if (saving) return;
    setError('');
    onClose();
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser) return setError('Please sign in as a teacher first.');
    if (existingStatus) return setError('This AI creation has already been nominated to the Overture Universe.');
    if (kind === 'shot_batch' ? !shotBatch?.videos.length : (['movie', 'game', 'audio'].includes(kind) || (kind === 'model_3d' && creation.type === 'video')) ? !creation.url : !imageUrl) return setError('This creation needs media before it can be nominated.');
    if (kind === 'game' && !safeGameUrl(creation.url)) return setError('Use an http or https game link.');
    if (!['player', 'world'].includes(kind) && !title.trim()) return setError('Give this card a title.');
    if (kind === 'player' && !player.name.trim()) return setError('Give the player a name.');
    if (kind === 'world' && !world.name.trim()) return setError('Give the world a name.');
    setSaving(true);
    setError('');
    try {
      const nominationId = `portfolio_${squadId}_${creation.id}`.replace(/[^a-zA-Z0-9_-]/g, '_');
      const nominationRef = doc(collection(db, 'libraryItems'), nominationId);
      if (kind === 'player') {
        await setDoc(nominationRef, {
          ...source,
          previewAssetUrl: imageUrl,
          status: 'nominated', contentType: 'character', title: player.name.trim(), description: player.detail.trim(),
          displayAssetUrl: imageUrl, originalAssetUrl: imageUrl,
          visibility: 'community', gameEligible: true, featured: true, defaultsVersion: 2, sourceType: 'student_creation',
          squadId,
          squadName,
          teamId: team.id,
          teamName: team.name,
          attribution: 'team_and_squad',
          character: {
            wears: player.wears, carries: player.carries, detail: player.detail,
            ability: player.ability, abilityDescription: player.abilityDescription,
            colors: player.colors.split(',').map((value) => value.trim()).filter(Boolean).slice(0, 3),
            traits: player.traits.split(',').map((value) => value.trim()).filter(Boolean).slice(0, 2),
          },
          sourcePortfolioCreationId: creation.id,
          submittedBy: currentUser.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
      } else if (kind === 'world') {
        const { name, ...worldDetails } = world;
        await setDoc(nominationRef, {
          ...source,
          previewAssetUrl: imageUrl,
          status: 'nominated', contentType: 'world', title: name.trim(), description: world.subtitle?.trim() || '',
          displayAssetUrl: imageUrl, originalAssetUrl: imageUrl,
          visibility: 'community', gameEligible: true, featured: true, defaultsVersion: 2, sourceType: 'student_creation',
          squadId,
          squadName,
          teamId: team.id,
          teamName: team.name,
          attribution: 'team_and_squad',
          world: worldDetails,
          sourcePortfolioCreationId: creation.id,
          submittedBy: currentUser.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
      }
      if (!['player', 'world'].includes(kind)) {
        const assetUrl = kind === 'shot_batch' ? shotBatch!.videos[0].url : (kind === 'movie' || kind === 'audio' || (kind === 'model_3d' && creation.type === 'video')) ? creation.url! : imageUrl;
        await setDoc(nominationRef, {
          ...source,
          previewAssetUrl: imageUrl,
          status: 'nominated', contentType: kind, title: title.trim(), description: description.trim(),
          displayAssetUrl: assetUrl, originalAssetUrl: assetUrl,
          ...(shotBatch ? { shotBatch } : {}),
          ...(kind === 'game' ? { game: { url: safeGameUrl(creation.url) } } : {}),
          ...(kind === 'model_3d' ? { model3d: { mediaType: creation.type === 'video' ? 'video' : 'image' } } : {}),
          visibility: 'community', gameEligible: false, featured: true, defaultsVersion: 2,
          sourceType: 'student_creation', squadId, squadName, teamId: team.id, teamName: team.name,
          attribution: 'team_and_squad', sourcePortfolioCreationId: creation.id,
          submittedBy: currentUser.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
      }
      setPlayer(playerInitial);
      setWorld(worldInitial);
      onNominated(creation.id);
      onClose();
      void window.appAlert(`${kind === 'player' ? 'Character' : kind.replace('_', ' ')} nominated for the Overture Universe!`);
    } catch (caught) {
      console.error(caught);
      setError('Could not nominate this creation for the Overture Universe.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cd-lightbox" role="presentation" onClick={close}>
      <form className="cd-showdown-modal" onSubmit={save} onClick={(event) => event.stopPropagation()}>
        <header>
          <div><span>OVERTURE UNIVERSE</span><h2>Nominate this creation</h2></div>
          <button type="button" className="cd-modal-close" onClick={close}>×</button>
        </header>
        <div className="cd-showdown-source">
          <div className="cd-showdown-reference-tabs" role="tablist" aria-label="Nomination reference">
            <button type="button" role="tab" aria-selected={referenceView === 'creation'} className={referenceView === 'creation' ? 'is-on' : ''} onClick={() => setReferenceView('creation')}>✨ AI Creation</button>
            <button type="button" role="tab" aria-selected={referenceView === 'photos'} className={referenceView === 'photos' ? 'is-on' : ''} disabled={!referencePhotos.length} onClick={() => setReferenceView('photos')}>📷 Student Photos {referencePhotos.length ? `(${referencePhotos.length})` : ''}</button>
          </div>
          <div className="cd-showdown-source-preview">
            {referenceView === 'creation' ? <LibraryMedia item={{ contentType: kind === 'player' ? 'character' : kind, title, ...(kind === 'model_3d' ? { model3d: { mediaType: creation.type === 'video' ? 'video' : 'image' } } : {}), ...(kind === 'game' ? { game: { url: creation.url || '' } } : {}), displayAssetUrl: (kind === 'movie' || kind === 'audio' || (kind === 'model_3d' && creation.type === 'video')) ? creation.url || '' : imageUrl, ...(shotBatch ? { shotBatch } : {}) }} /> : referencePhotos[photoIndex] ? <>
              <img src={referencePhotos[photoIndex].url} alt={`Student reference ${photoIndex + 1} of ${referencePhotos.length}`} />
              {referencePhotos.length > 1 && <div className="cd-showdown-photo-controls">
                <button type="button" aria-label="Previous student photo" onClick={() => setPhotoIndex(index => (index - 1 + referencePhotos.length) % referencePhotos.length)}>‹</button>
                <span>{photoIndex + 1} / {referencePhotos.length}</span>
                <button type="button" aria-label="Next student photo" onClick={() => setPhotoIndex(index => (index + 1) % referencePhotos.length)}>›</button>
              </div>}
            </> : null}
          </div>
          <div className="cd-showdown-source-meta"><small>Already connected</small><b>{squadName}</b><span>{team.name}</span></div>
        </div>
        <div className="cd-showdown-kind" role="group" aria-label="Creation type">
          {(shotBatch ? ['shot_batch'] : creation.type === 'audio' ? ['audio'] : creation.type === 'model_3d' ? ['model_3d'] : creation.type === 'video' ? ['movie', 'model_3d'] : ['game_link', 'claude_artifact', 'link'].includes(creation.type || '') ? ['game'] : ['player', 'world', 'comic', 'poster', 'model_3d']).map(value => <button key={value} type="button" className={kind === value ? 'is-on' : ''} onClick={() => setKind(value as Kind)}>{value === 'model_3d' ? '3D model' : value === 'movie' ? 'Video' : value === 'shot_batch' ? 'Shot batch' : value === 'player' ? 'Character' : value[0].toUpperCase() + value.slice(1)}</button>)}
        </div>
        {shotBatch && <p>{shotBatch.videos.length} videos will be included in shot order, with every uploaded option.</p>}
        <div className="cd-showdown-fields">
          {kind === 'player' ? <>
            <label>Player name *<input value={player.name} onChange={(e) => setPlayer({ ...player, name: e.target.value })} placeholder="Nova Bolt" /></label>
            <label>Something they wear<input value={player.wears} onChange={(e) => setPlayer({ ...player, wears: e.target.value })} /></label>
            <label>Something they carry or use<input value={player.carries} onChange={(e) => setPlayer({ ...player, carries: e.target.value })} /></label>
            <label>One recognizable detail<input value={player.detail} onChange={(e) => setPlayer({ ...player, detail: e.target.value })} /></label>
            <label>Special ability<input value={player.ability} onChange={(e) => setPlayer({ ...player, ability: e.target.value })} /></label>
            <label className="cd-showdown-wide">What happens when they use it?<input value={player.abilityDescription} onChange={(e) => setPlayer({ ...player, abilityDescription: e.target.value })} /></label>
            <label>Colors, separated by commas<input value={player.colors} onChange={(e) => setPlayer({ ...player, colors: e.target.value })} placeholder="blue, yellow" /></label>
            <label>Two personality traits<input value={player.traits} onChange={(e) => setPlayer({ ...player, traits: e.target.value })} placeholder="brave, silly" /></label>
          </> : kind === 'world' ? <>
            <label>World name *<input value={world.name} onChange={(e) => setWorld({ ...world, name: e.target.value })} placeholder="Happy California" /></label>
            <WorldFields world={world} onChange={details => setWorld({ ...details, name: world.name })} />
          </> : <>
            <label>Title *<input required value={title} onChange={e => setTitle(e.target.value)} /></label>
            <label>Description<textarea value={description} onChange={e => setDescription(e.target.value)} /></label>
          </>}
        </div>
        {error && <p className="cd-showdown-error">{error}</p>}
        <footer><button type="button" className="cd-btn" onClick={close}>Cancel</button><button type="submit" className="cd-btn cd-btn-primary" disabled={saving || Boolean(existingStatus)}>{existingStatus ? 'Already nominated' : saving ? 'Submitting…' : `Nominate ${kind === 'player' ? 'character' : kind === 'movie' ? 'video' : kind === 'model_3d' ? '3D model' : kind.replace('_', ' ')} for the Universe`}</button></footer>
      </form>
    </div>
  );
}
